import { useLazyQuery } from '@apollo/react-hooks'
import { DateRanges } from '@berlitzplatforms/micro.ui.scheduling-calendar/lib'
import PortalStudentProfile from '@classes/StudentProfile'
import Error from '@components/Error'
import { BookingPossibilityQueryResult } from '@interfaces/BookingPossibilities'
import { ScheduleSession } from '@interfaces/ScheduleSession'
import { GET_BOOKING_POSSIBILITIES } from '@queries/getBookingPossibilities'
import { GET_SCHEDULES_ID } from '@queries/index'
import { CANCELLED_LESSON_STATUS, MINS_PER_UNIT } from '@utils/constants'
import { groupBy } from 'lodash'
import * as Moment from 'moment-timezone'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import Spacer from '@berlitz/spacer'
import AvailabilityPreview from '@berlitzplatforms/micro.ui.availability-preview'
import { InstructorHours, InstructorHoursQueryRes } from '@interfaces/InstructorHours'
import { LearningCenterHoliday } from '@interfaces/LearningCenter'
import { GET_INSTRUCTOR_HOURS } from '@queries/instructorHours'
import { GET_LC_HOLIDAYS } from '@queries/learningCenters'
import { extendMoment } from 'moment-range'
import Skeleton from 'react-loading-skeleton'
import { Button } from './style'
import { FMIStudentProfileContext, useBlockedInstructors } from '@layouts/Scheduling/Instructors/context'

export interface AvailabilityPreviewWrapperProps {
  loading: boolean
  instructor?: string
}

const AvailabilityPreviewWrapper: React.FC<AvailabilityPreviewWrapperProps> = ({
  loading: pageLoading,
  instructor: instructorId,
}) => {
  const moment = extendMoment(Moment)
  const router = useRouter()
  const intl = useIntl()
  const blocked = useBlockedInstructors()
  const { userProfile: profile, baseRoute, getRouteParams } = useContext(FMIStudentProfileContext)
  const student = new PortalStudentProfile(profile.StudentProfile.StudentProfileInfo)
  const program = student.Programs[router.query.programId as string]
  const [availabilities, setAvailabilities] = useState<DateRanges>()
  const [unavailabilities, setUnavailabilities] = useState<DateRanges>()
  const [finalData, setFinalData] = useState<DateRanges>()
  const [error, setError] = useState(false)
  const [instructor, setInstructor] = useState(instructorId)
  const [hoverRange] = useState((program.program?.UnitsPerLesson || 0) * MINS_PER_UNIT)
  const [loading, setIsLoading] = useState(pageLoading)

  const [numOfDays] = useState(7)
  const [range] = useState({
    start: moment.tz(profile.Timezone),
    end: moment.tz(profile.Timezone).add(numOfDays, 'days').startOf('day'),
  })
  const [instructorHours, setInstructorHours] = useState<InstructorHours>()

  const [getIH, { data: IHData, loading: IHLoading, error: IHerror }] = useLazyQuery<InstructorHoursQueryRes>(
    GET_INSTRUCTOR_HOURS,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [getStudentSessions, { data: myCalendar, loading: SSLoading, error: myCalendarError }] = useLazyQuery<{
    getSchedulesId: ScheduleSession[]
  }>(GET_SCHEDULES_ID, { fetchPolicy: 'no-cache' })

  const [getInstructorSessions, { data: instructorSessions, loading: ISLoading, error: instructorSessionsError }] =
    useLazyQuery<{
      getSchedulesId: ScheduleSession[]
    }>(GET_SCHEDULES_ID, { fetchPolicy: 'no-cache' })

  const [getBookingPossibilities, { data: bookingP, loading: BPLoading, error: bookingPError }] =
    useLazyQuery<BookingPossibilityQueryResult>(GET_BOOKING_POSSIBILITIES, { fetchPolicy: 'no-cache' })

  const [getLCHolidays, { data: LCHolidays, loading: LCHLoading, error: LCHolidaysError }] =
    useLazyQuery<LearningCenterHoliday>(GET_LC_HOLIDAYS)

  const convertToDateRange = (dates) =>
    dates?.map((date) => [date.StartTime || date.StartDateTime, date.EndTime || date.EndDateTime])

  const prepSessions = (data?: ScheduleSession[]) => {
    return Object.entries(
      groupBy(
        data?.filter((item) => !CANCELLED_LESSON_STATUS.includes(item.Status?.toLowerCase())),
        'SessionId'
      )
    ).reduce((prev, curr) => {
      return [...prev, [curr[1][0].StartTime, curr[1][curr[1].length - 1].EndTime]] as DateRanges
    }, [] as DateRanges)
  }

  const fetchDetails = () => {
    const end = range.end.clone().add(2, 'day').startOf('day')
    const start = range.start.clone()

    getStudentSessions({
      variables: {
        Id: profile.SFId,
        FromDate: start.weekday(0).startOf('day').utc().format(),
        EndDate: end.toISOString(),
        FilterType: 'FILTER_STUDENT_MY_CALENDER',
        Limit: 10000,
        UserId: profile.SFId
      },
    })

    getBookingPossibilities({
      variables: {
        StartDateTime: start.utc().format().replace('T', '+'),
        EndDateTime: end.toISOString().replace('T', '+'),
        ProgramId: router.query.programId,
        InstructorId: instructor,
      },
    })

    getInstructorSessions({
      variables: {
        Id: instructor || router.query.instructorId,
        FromDate: start.utc().format(),
        EndDate: end.toISOString(),
        FilterType: 'FILTER_INSTRUCTOR_MY_CALENDER',
        Limit: 10000,
        UserId: profile.SFId
      },
    })

    getIH({
      variables: {
        StartDateTime: start.toISOString().replace('T', '+'),
        EndDateTime: end.toISOString().replace('T', '+'),
        LearningCenterId: program.program?.LearningCenterId,
        InstructorId: instructor || router.query.instructorId,
      },
    })

    getLCHolidays({
      variables: {
        StartDateTime: start.toISOString().replace('T', '+'),
        EndDateTime: end.toISOString().replace('T', '+'),
        LearningCenterId: program.program?.LearningCenterId,
        Country: program.program?.DeliveryLCCountry,
      },
    })
  }

  useEffect(() => {
    setIsLoading(IHLoading || ISLoading || SSLoading || BPLoading || LCHLoading)
  }, [IHLoading, ISLoading, SSLoading, BPLoading, LCHLoading])

  useEffect(() => {
    if (instructorHours && instructorSessions && LCHolidays?.getLCHolidays && myCalendar?.getSchedulesId) {
      const maxedDays = instructorHours.instructorHoursInfo?.DailyHoursInfo?.filter((item) => {
        const dh = item.DailyHoursRemaining || 0
        return dh <= 0 || dh < hoverRange / 60
      })

      const programEnd = moment.tz(program.program?.ContractEndDate, 'GMT')
      const cutOffDate = program
        .getCutOffTime(profile.Timezone, program.program?.SchedulingMinutesBeforeCutoff)
        .toISOString()
      setUnavailabilities(
        (
          convertToDateRange(
            instructorSessions?.getSchedulesId?.filter(
              (item) => !CANCELLED_LESSON_STATUS.includes(item.Status?.toLowerCase())
            )
          ) || []
        )
          .concat(
            convertToDateRange(maxedDays).concat(
              convertToDateRange(LCHolidays.getLCHolidays.Holidays?.map((holiday) => ({ ...holiday.holidayInfo })))
            )
          )
          .concat(prepSessions(myCalendar?.getSchedulesId))
          // remove past availabilities
          .concat([
            [moment.tz(profile.Timezone).startOf('day').toISOString(), moment.tz(profile.Timezone).toISOString()],
          ])
          // limit availability until program end date
          .concat([[programEnd.toISOString(), programEnd.clone().add(7, 'days').toISOString()]])
          .concat([[moment.tz(profile.Timezone).startOf('d').toISOString(), cutOffDate]])
      )
    }
  }, [instructorSessions, JSON.stringify(instructorHours), JSON.stringify(LCHolidays), myCalendar])

  useEffect(() => {
    setInstructorHours(IHData?.getInstructorHours)
  }, [IHData])

  useEffect(() => {
    const avail = convertToDateRange(bookingP?.getBookingPossibilities?.bookingPossibilitiesDateRange)
    setAvailabilities(avail)
  }, [bookingP])

  useEffect(() => {
    setError(!!(myCalendarError || instructorSessionsError || bookingPError || IHerror || LCHolidaysError))
  }, [myCalendarError, instructorSessionsError, bookingPError, IHerror, LCHolidaysError])

  useEffect(() => {
    if (!instructor) {
      return
    }
    setFinalData(undefined)
    fetchDetails()
  }, [instructor, range])

  useEffect(() => {
    setInstructor(instructorId)
  }, [instructorId])

  useEffect(() => {
    if (availabilities && unavailabilities && !loading) {
      const manipulatedData = unavailabilities.reduce((prev, cur) => {
        const newPrev = prev
          .reduce((p1, c1) => {
            const unavail = moment.range(moment(cur[0]), moment(cur[1]))
            const avail = moment.range(moment(c1[0]), moment(c1[1]))
            const intersection = avail.intersect(unavail)

            if (intersection) {
              const diff = avail.subtract(intersection)
              diff
                ? diff.map((result) => {
                    p1.push([result.start.toISOString(), result.end.toISOString()])
                  })
                : null
            } else {
              p1.push(c1)
            }
            return p1
          }, [] as DateRanges)
          .filter(Boolean)

        return newPrev
      }, availabilities)

      setFinalData(manipulatedData)
    }
  }, [availabilities, unavailabilities])

  return error ? (
    <Error statusCode="400" />
  ) : finalData && !loading ? (
    <>
      <AvailabilityPreview
        timezone={profile.Timezone}
        title={intl.formatMessage({ id: 'Availability' })}
        availability={finalData}
        availableText={intl.formatMessage({ id: 'Available' })}
        unavailableText={intl.formatMessage({ id: 'Unavailable' })}
        morningText={intl.formatMessage({ id: 'Morning' })}
        afternoonText={intl.formatMessage({ id: 'Afternoon' })}
        nightText={intl.formatMessage({ id: 'Night' })}
      />
      <Spacer size="lg" />
      <div className="body">
        <Button
          outline
          onClick={() => {
            router.push(`${baseRoute}/availability?${getRouteParams()}`)
          }}
          disabled={pageLoading || loading || blocked.isBlocked(instructor)}
        >
          <FormattedMessage id="Check availability" defaultMessage="Check availability" />
        </Button>
      </div>
    </>
  ) : (
    <>
      <Skeleton width="356px" height="179px" />
      <Spacer size="lg" />
      <div className="body">
        <Skeleton width="150px" height="30px" />
      </div>
    </>
  )
}

export default AvailabilityPreviewWrapper